import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Heading, HStack, Text, VStack } from '@bonusly/design-system';

import { Bizy } from '../../components/bizy';
import { useBizySidePanelStore } from '../../components/bizy-side-panel/store/use-bizy-side-panel-store';
import { useBizyWidgetStore } from '../../components/bizy-side-panel/store/use-bizy-widget-store';
import { ContentContainer } from '../../components/layout/content-container';
import { useBizyUserConversations } from '../../queries/bizy-conversations';
import type { BizyUserMessage } from '../../schemas/bizy-conversations';
import { BizyHistoryEmptyState } from './components/bizy-history-empty-state';
import { ConversationDetail } from './components/conversation-detail';
import { ConversationList } from './components/conversation-list';

const PER_PAGE = 20;

export const BizyHistory: FC = () => {
	const { t } = useTranslation('bizy_history');
	const { track } = useAnalytics();
	const [page, setPage] = useState(1);
	const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

	const { data, isLoading } = useBizyUserConversations({ page, per_page: PER_PAGE });
	const [conversations, meta] = data ?? [undefined, undefined];

	const widgetStore = useBizyWidgetStore();
	const openSidePanel = useBizySidePanelStore((s) => s.open);
	const showChat = useBizySidePanelStore((s) => s.showChat);

	// Track page view
	useEffect(() => {
		track(AnalyticsEvent.BizyHistory.PageViewed);
	}, [track]);

	const handleSelectConversation = useCallback(
		(conversationId: string) => {
			track(AnalyticsEvent.BizyHistory.ConversationViewed, { conversationId });
			setSelectedConversationId(conversationId);
		},
		[track]
	);

	const handleBack = useCallback(() => {
		setSelectedConversationId(null);
	}, []);

	const handleResumeConversation = useCallback(
		(conversationId: string, messages: BizyUserMessage[]) => {
			track(AnalyticsEvent.BizyHistory.ConversationResumed, { conversationId });
			widgetStore.setResume(conversationId, messages);
			showChat();
			openSidePanel();
		},
		[track, widgetStore, showChat, openSidePanel]
	);

	const handlePageChange = useCallback((newPage: number) => {
		setPage(newPage);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	const isEmpty = !isLoading && conversations?.length === 0;

	return (
		<ContentContainer>
			{selectedConversationId ? (
				<ConversationDetail conversationId={selectedConversationId} onBack={handleBack} onResumeConversation={handleResumeConversation} />
			) : (
				<VStack spacing={6} align="stretch">
					{/* Page header */}
					<HStack spacing={3}>
						<Box>
							<Bizy state="curious" size="sm" />
						</Box>
						<VStack spacing={0} align="flex-start">
							<Heading size="lg" color="text.default">
								{t('page.title')}
							</Heading>
							<Text fontSize="sm" color="text.soft">
								{t('page.description')}
							</Text>
						</VStack>
					</HStack>

					{/* Content */}
					{isEmpty ? (
						<BizyHistoryEmptyState />
					) : (
						<ConversationList
							conversations={conversations}
							isLoading={isLoading}
							page={meta?.page ?? 1}
							totalPages={meta?.totalPages ?? 0}
							totalCount={meta?.totalCount ?? 0}
							onSelectConversation={handleSelectConversation}
							onPageChange={handlePageChange}
						/>
					)}
				</VStack>
			)}
		</ContentContainer>
	);
};
